import { type ReactNode, useMemo, useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableRow } from "~/common/components/Table/TableElements";
import { CellWithSubtitle } from "~/features/productDetails/components/DataTables/CellWithSubtitle";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { useSendOrderFeedback } from "~/features/orders/orderDetails/hooks/useSendOrderFeedback/useSendOrderFeedback";
import { ManualPackageRestrictionContext } from "~/features/PackageRestriction/PackageRestrictionContext";
import OrderTableProduct from "~/features/orders/orderDetails/components/OrderDetails/OrderTableProduct";
import { getPackagingLabelAtLineItem } from "~/features/cart/cart/utils/cartUtils";
import { fakeDescriptorFromProduct, getTotalPrice } from "~/domains/product/utils/productDescriptorUtils";
import { getAliases } from "~/domains/product/components/ProductAliases";
import { ParamComponent, ParamsGrid } from "~/common/components/ParamsGrid";
import { DateFormatType, formatDate, formatNullableDate } from "~/common/utils/DateFormatter";
import { WeightFormat } from "~/domains/product/components/WeightFormat";
import Chip from "~/common/components/Chip/Chip";
import { useTranslation, type WithTranslation } from "react-i18next";
import { NewVersionMessage } from "~/common/components/Messages/NewVersionMessage";
import { isLineLevelCustomerOrderNumberEnabled } from "~/utils/features";
import { OrderLineItemElements } from "~/features/orders/common/components/OrderLineItemElements";
import { type OrderData, type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { hasSuccessor } from "~/utils/productUtils";
import { TotalNetPrice as BaseTotalPrice } from "~/domains/pricing/components/Price/v2/NetPrice";
import { IncludedSurcharge } from "~/domains/product/components/ProductParams";
import ServiceCasesTable from "~/features/orders/orderDetails/components/ServiceCases/ServiceCasesTable";
import OrderSubLineTable from "~/features/orders/common/components/OrderSubLineTable";
import { translateWarehouseName } from "~/utils/language/translationUtils";
import { Snackbar, SnackbarCloseButton } from "~/common/mui/Snackbar/Snackbar";
import { Fade } from "~/common/mui";
import { PriceReferenceParam } from "~/features/cart/cart/components/LineItemStaticParams";
import { formatShippingOrDeliveryDate } from "~/common/utils/dateUtils";
import { getStatusData } from "~/features/orders/orderList/components/orderLineStatusData";
import { type OrderLineRowProps } from "~/features/orders/orderList/components/orderLineTableTypes";

export { getStatusData } from "~/features/orders/orderList/components/orderLineStatusData";
export type { OrderLineRowProps as RowProps } from "~/features/orders/orderList/components/orderLineTableTypes";

interface Props {
  data: OrderLineItemData[];
  orderData: OrderData;
  soNumber?: string;
  poNumber?: string;
}
export const OrderLineTable = ({ data, orderData, soNumber, poNumber }: Props) => {
  const { t } = useTranslation();

  return (
    <Table className={"multi-body"}>
      <TableHead>
        <TableRow>
          <TableCell>
            <CellWithSubtitle title={t("quoteDetailsPage.summary.orderLineNumber")} />
          </TableCell>
          <TableCell>
            <CellWithSubtitle title={t("quoteDetailsPage.table.header.product")} />
          </TableCell>
          <TableCell>
            <CellWithSubtitle
              title={t("quoteListPage.table.header.confirmedQuantity")}
              subtitle={t("quoteListPage.table.header.requestedQuantity")}
            />
          </TableCell>
          <TableCell>
            <CellWithSubtitle
              title={t("quoteDetailsPage.summary.packagingCode")}
              subtitle={t("cartPage.table.header.packaging.subtitle")}
            />
          </TableCell>
          <TableCell>
            <CellWithSubtitle
              title={t("quoteListPage.table.header.estimatedDeliveryDate")}
              subtitle={t("addToCart.contract.rdd")}
            />
          </TableCell>
          <TableCell>
            <CellWithSubtitle
              title={t("activeQuotePage.table.weight")}
              subtitle={t("orderDetailsPage.labels.weightUnit")}
            />
          </TableCell>
          <TableCell>
            <CellWithSubtitle
              title={t("orderDetailsPage.table.header.price")}
              subtitle={t("orderDetailsPage.table.header.priceUnit")}
            />
          </TableCell>
          <TableCell>
            <CellWithSubtitle title={t("orderDetailsPage.table.header.total")} />
          </TableCell>
          <TableCell>
            <CellWithSubtitle title={t("orderDetailsPage.table.header.status")} />
          </TableCell>
          <TableCell />
        </TableRow>
      </TableHead>

      {data.map((entry, index) => (
        <OrderLineTableRow
          data={entry}
          key={index}
          lineItemIndex={index}
          orderData={orderData}
          soNumber={soNumber}
          poNumber={poNumber}
        />
      ))}
    </Table>
  );
};

const OrderLineTableRow = ({ data, orderData, lineItemIndex, soNumber, poNumber }: OrderLineRowProps) => {
  const { t } = useTranslation();
  const contactSchaefflerPermission = userHasPermission(Permission.ORDER_CONTACT);

  const [snackbarData, setSnackbarData] = useState<{
    open: boolean;
    message: string;
  }>({
    open: false,
    message: "",
  });

  const [popups, open, userHasCreatedCase] = useSendOrderFeedback(
    data,
    orderData,
    lineItemIndex,
    soNumber,
    poNumber,
    setSnackbarData,
  );

  const [hasCreatedSubLineCase, setHasCreatedSubLineCase] = useState(false);

  const haveSubEntries = data.subEntries && data?.subEntries?.length > 0;

  const dataCellsSubEntries = useMemo(() => {
    return (
      <>
        <TableCell />
        <TableCell colSpan={9} className={"scha__order-list__deliveries"}>
          <span> {t("order.deliveries.label")}</span>
        </TableCell>
        {data.subEntries?.map((subEntry, index) => (
          <OrderSubLineTable
            index={index}
            subEntry={subEntry}
            contactSchaefflerPermission={contactSchaefflerPermission}
            data={data}
            orderData={orderData}
            lineItemIndex={lineItemIndex}
            soNumber={soNumber}
            poNumber={poNumber}
            setSnackbarData={setSnackbarData}
            setHasCreatedCaseSpinner={setHasCreatedSubLineCase}
          />
        ))}
      </>
    );
  }, [data]);

  return (
    <ManualPackageRestrictionContext restrictedPackNo={data.packCode}>
      <TableBody>
        <TableRow>
          <TableCell>{data.lineNo}</TableCell>
          <TableCell>
            <OrderTableProduct product={data.product} />
          </TableCell>
          <TableCell>
            {haveSubEntries ? (
              <>{sumValues4HeaderOrders(data, "quantity")}</>
            ) : (
              <CellWithSubtitle
                title={data.deliveryDate ? data.confirmedQty ?? "-" : "-"}
                subtitle={data.qty}
                lightHeader={true}
              />
            )}
          </TableCell>
          <TableCell>
            <CellWithSubtitle
              title={getPackagingLabelAtLineItem({
                t,
                productDescriptor: fakeDescriptorFromProduct(data.product),
                packCode: data.packCode,
                packSize: data.packSize,
                quantity: haveSubEntries ? data.subEntries?.[0].qty ?? data.qty : data.qty,
              })}
              subtitle={translateWarehouseName(t, data.location)}
              lightHeader={true}
            />
            {getAliases(data.product, "15-digits", data.packCode).length > 0 && (
              <ParamComponent
                label={t("product.customerId")}
                value={getAliases(data.product, "15-digits", data.packCode).join(", ")}
              />
            )}
            {isLineLevelCustomerOrderNumberEnabled() && data.customerOrderNumber && (
              <ParamComponent label={t("search.type.customerOrderNumber")} value={data.customerOrderNumber} />
            )}
          </TableCell>
          <TableCell>
            {haveSubEntries ? (
              <>{sumValues4HeaderOrders(data, "deliverDate")}</>
            ) : (
              <CellWithSubtitle
                title={formatShippingOrDeliveryDate(data.deliveryDate, DateFormatType.DATE)}
                subtitle={formatNullableDate(data.requestedDeliveryDate, DateFormatType.DATE)}
                lightHeader={true}
              />
            )}
          </TableCell>
          <TableCell>
            {haveSubEntries ? (
              <CellWithSubtitle
                title={data.subEntries && <WeightFormat weight={data.subEntries[0].weight} />}
                subtitle={t("common.product.perPiece", { count: 1 })}
                lightHeader={true}
              />
            ) : (
              <CellWithSubtitle
                title={<WeightFormat weight={data.weight} />}
                subtitle={t("common.product.perPiece", { count: 1 })}
                lightHeader={true}
              />
            )}
          </TableCell>
          <TableCell>
            <OrderLineItemElements.NetPrice data={data} />
            <PriceReferenceParam className={"mt-2"} product={data.product} lineItem={data} />
          </TableCell>
          <TableCell>
            {haveSubEntries ? (
              <>{sumValues4HeaderOrders(data, "netValue")}</>
            ) : (
              <>
                <OrderLineItemElements.TotalPrice data={data} />
              </>
            )}
          </TableCell>
          <TableCell>
            {!haveSubEntries ? (
              <>
                <CellWithSubtitle
                  title={<Chip key={"chip"}>{t(data.status)}</Chip>}
                  lightHeader={true}
                  subtitle={
                    <ParamsGrid
                      key={`invoice-${data.lineNo}`}
                      params={getStatusData(t, data)}
                      noColumnFlex={true}
                    />
                  }
                />
                <ParamsGrid
                  key={`order-list-quote-nota-fiscal-${data.lineNo}`}
                  params={getQuoteNotaFiscalData(t, data)}
                  noColumnFlex={true}
                />
              </>
            ) : (
              <div className={"scha__order-list-nota-fiscal"}>
                <ParamsGrid
                  key={`order-list-quote-nota-fiscal-${data.lineNo}`}
                  params={getQuoteNotaFiscalData(t, data)}
                  noColumnFlex={true}
                />
              </div>
            )}
          </TableCell>
          <TableCell />
        </TableRow>
        <TableRow>
          <TableCell />
          <TableCell colSpan={9}>
            {contactSchaefflerPermission && (
              <Snackbar
                onClose={() => setSnackbarData((prev) => ({ ...prev, open: false }))}
                open={snackbarData.open}
                autoHideDuration={7000}
                TransitionComponent={Fade}
                transitionDuration={{ exit: 500, appear: 500, enter: 500 }}
                message={snackbarData.message}
                action={<SnackbarCloseButton onClick={() => setSnackbarData((prev) => ({ ...prev, open: false }))} />}
              />
            )}
            {contactSchaefflerPermission && (
              <ServiceCasesTable
                lineNumber={data.lineNo}
                openPopup={open}
                serviceCase={orderData.serviceCases?.[data.lineNo]}
                orderNumber={soNumber}
                userHasCreatedCase={userHasCreatedCase || hasCreatedSubLineCase}
                setSnackbar={setSnackbarData}
                isOdd={lineItemIndex % 2 === 0}
              />
            )}
          </TableCell>
        </TableRow>
        {hasSuccessor(data.product) && (
          <TableRow>
            <TableCell />
            <TableCell colSpan={3}>
              <NewVersionMessage baseProduct={data.product} />
            </TableCell>
            <TableCell colSpan={5} />
          </TableRow>
        )}

        {data.subEntries && data.subEntries?.length > 0 && <>{dataCellsSubEntries}</>}
      </TableBody>
      {popups}
    </ManualPackageRestrictionContext>
  );
};

export interface GridDataItem {
  label: ReactNode;
  value: JSX.Element | string | undefined;
}

export function getQuoteNotaFiscalData(t: WithTranslation["t"], entry: OrderLineItemData) {
  const gridData: [GridDataItem[]] = [[]];

  if (entry.notaFiscal && entry.notaFiscal.trim().length > 0) {
    gridData[0].push({
      label: t("quoteDetailsPage.summary.notaFiscal"),
      value: entry.notaFiscal,
    });
  }

  if (entry.quoteNumber && entry.quoteNumber.length > 1) {
    gridData[0].push({
      label: t("quoteDetailsPage.summary.quoteNumber"),
      value: entry.quoteNumber,
    });
  }

  return gridData;
}

export const sumValues4HeaderOrders = (
  data: OrderLineItemData,
  type: "quantity" | "deliverDate" | "netValue" | "surcharge",
) => {
  switch (type) {
    case "quantity":
      if (data.subEntries && data.subEntries.length > 0) {
        let sumQty = 0;
        data.subEntries.map((entry) => (sumQty += entry.confirmedQty));
        return <CellWithSubtitle lightHeader={true} title={sumQty} subtitle={data.subEntries?.[0].qty} />;
      }
      return <CellWithSubtitle lightHeader={true} title={data.confirmedQty} subtitle={data.qty} />;
    case "deliverDate":
      if (data.subEntries && data.subEntries.length > 0) {
        const arrDates: Date[] = [];
        data.subEntries.map((item) => item.deliveryDate && arrDates.push(item.deliveryDate));
        const requestedDate = data.subEntries?.[0].requestedDeliveryDate ?? undefined;
        const oldestDate: Date = new Date(Math.min(...arrDates.map((date) => date.getTime())));
        const title = formatDate(oldestDate, DateFormatType.DATE) + (arrDates.length > 1 ? ", ..." : "");
        return (
          <CellWithSubtitle
            lightHeader={true}
            title={title}
            subtitle={requestedDate && formatDate(requestedDate, DateFormatType.DATE)}
          />
        );
      }
      return (
        <CellWithSubtitle
          lightHeader={true}
          title={data.deliveryDate ? formatDate(data.deliveryDate, DateFormatType.DATE) : "-"}
          subtitle={data.requestedDeliveryDate ? formatDate(data.requestedDeliveryDate, DateFormatType.DATE) : "-"}
        />
      );
    case "netValue":
      // eslint-disable-next-line no-case-declarations
      let sumNetValue = 0;
      if (data.subEntries && data.subEntries.length > 0) {
        data.subEntries.map((entry) => {
          const totalPrice = getTotalPrice(entry.netPrice, entry.confirmedQty)?.value ?? 0;
          sumNetValue += totalPrice;
        });
        const currency = data.subEntries?.[0].priceForTotal.currencyIso ?? "";
        const totalNetValue = {
          value: sumNetValue,
          baseValue: sumNetValue,
          currencyIso: currency,
          isSpecial: undefined,
          isSubscription: false,
          rebate: 0,
          surcharge: 0,
          unit: 1,
        };
        return (
          <BaseTotalPrice
            price={totalNetValue}
            classes={{
              root: "d-flex flex-direction-column",
              price: "f-bold scha__order-table__header__title",
            }}
          />
        );
      }
      // eslint-disable-next-line no-case-declarations
      const totalPriceSingle = getTotalPrice(data.netPrice, data.qty);
      return (
        <BaseTotalPrice
          price={totalPriceSingle}
          classes={{
            root: "d-flex flex-direction-column",
            price: "f-bold scha__order-table__header__title",
          }}
        />
      );
    case "surcharge":
      if (data.subEntries && data.subEntries.length > 0) {
        let sumSurcharge = 0;
        data.subEntries.map((entry) => (sumSurcharge += entry.netPrice.surcharge));
        const currencySurcharge = data.subEntries?.[0].netPrice.currencyIso ?? "";
        const surcharge = {
          currencyIso: currencySurcharge,
          value: sumSurcharge,
        };
        if (surcharge.value === 0) {
          return null;
        }
        return <IncludedSurcharge value={surcharge} percent={surcharge.value} />;
      }
      if (data.netPrice.surcharge === 0) {
        return null;
      }
      return <IncludedSurcharge value={data.netPrice} percent={data.netPrice.surcharge} />;
    default:
      break;
  }
};
